import type { Schemas } from "@fluid-app/fluid";

import type { CartData } from "../../cart-widget/mockData";
import CartWidget from "../../cart-widget/CartWidget";
import { usePlaylist } from "../../media-widget/hooks/usePlaylistContext";

const CartWidgetContainer = ({
  alwaysOpen,
  hideWidget,
}: {
  alwaysOpen: boolean;
  hideWidget?: boolean;
}) => {
  const { cartItems, setCartItems } = usePlaylist();

  if (typeof window === "undefined") {
    return null; // Return null during server-side rendering
  }

  const { addCartItems, checkout } = window.FluidCommerceSDK;

  const adjustCartItems = (
    cartItems: CartData,
    variantIds: number[],
    newQuantity: number,
  ) => {
    // Create a new items array with the updated quantity for the specific variant
    const updatedItems = (cartItems.items ?? []).map((item) =>
      variantIds.includes(item.variant?.id ?? 0)
        ? { ...item, quantity: newQuantity }
        : item,
    );

    const cost = updatedItems
      .reduce((acc, item) => {
        return (
          acc +
          (item.quantity ?? 0) *
            Number((item.variant?.price ?? "0").replace(/[^0-9.]/g, ""))
        );
      }, 0)
      .toFixed(2);

    setCartItems((prev: CartData) => ({
      ...prev,
      items: updatedItems,
      sub_total_in_currency: `$${cost}`,
    }));
  };

  const overrideCheckout = async () => {
    await addCartItems(
      (cartItems.items ?? [])
        .filter(
          (
            item,
          ): item is typeof item & { variant: { id: number } } & {
            quantity: number;
          } => item.variant?.id !== undefined && item.quantity !== undefined,
        )
        .map((item) => ({
          variant_id: item.variant.id,
          quantity: item.quantity,
        })),
    );
    await checkout();
  };

  const overrideIncrementCart = (variantId: number) => {
    const currentQty =
      cartItems.items?.find((item) => item.variant?.id === variantId)
        ?.quantity ?? 0;
    adjustCartItems(cartItems, [variantId], currentQty + 1);
  };

  const overrideDecrementCart = (variantId: number) => {
    const currentQty =
      cartItems.items?.find((item) => item.variant?.id === variantId)
        ?.quantity ?? 0;
    adjustCartItems(cartItems, [variantId], Math.max(currentQty - 1, 0));
  };

  const overrideRemoveEnrollmentPack = () => {
    const variantIds =
      cartItems.items
        ?.filter((item: Schemas["CartItem"]) => item.enrollment)
        .map((item: Schemas["CartItem"]) => item.variant?.id ?? 0) ?? [];
    adjustCartItems(cartItems, variantIds, 0);
  };

  const overrideEnrollmentPackAdd = () => {
    const variantIds =
      cartItems.items
        ?.filter((item: Schemas["CartItem"]) => item.enrollment)
        .map((item: Schemas["CartItem"]) => item.variant?.id ?? 0) ?? [];
    adjustCartItems(cartItems, variantIds, 1);
  };

  return (
    <CartWidget
      alwaysOpen={alwaysOpen}
      hideWidget={hideWidget}
      overrideCart={cartItems}
      onCheckout={overrideCheckout}
      onIncrementCart={overrideIncrementCart}
      onDecrementCart={overrideDecrementCart}
      onRemoveEnrollmentPack={overrideRemoveEnrollmentPack}
      onEnrollmentPackAdd={overrideEnrollmentPackAdd}
    />
  );
};

export default CartWidgetContainer;
